import { useEffect, useState } from 'react';
import { searchProperties } from '../../../services/ragSearchService';
import { fetchBrokerMap } from '../../../services/brokerMapService';

/**
 * Platform trust stats for the "Why Safaqat 24" band. Every value is derived from
 * a real endpoint; anything we can't back with data (e.g. total listed value in
 * SAR) is simply omitted rather than invented. `null` = still loading / no data.
 */
export interface HomeStats {
  /** Total live (REGA-verified) listings — from the search index total. */
  listings: number | null;
  /** Distinct verified brokers on the broker map. */
  brokers: number | null;
  /** Distinct cities those brokers operate in ("cities covered"). */
  cities: number | null;
  loading: boolean;
}

// Cache once per process — these barely change within a session.
let cached: Omit<HomeStats, 'loading'> | null = null;

export function useHomeStats(): HomeStats {
  const [stats, setStats] = useState<Omit<HomeStats, 'loading'>>(
    () => cached ?? { listings: null, brokers: null, cities: null },
  );
  const [loading, setLoading] = useState(!cached);

  useEffect(() => {
    if (cached) return;
    let cancelled = false;

    (async () => {
      const [listingsRes, brokers] = await Promise.all([
        searchProperties({ limit: 1 }).catch(() => null),
        fetchBrokerMap({ verified: true }).catch(() => []),
      ]);

      const listings =
        listingsRes && listingsRes.success && listingsRes.data
          ? listingsRes.data.pagination.total
          : null;

      const brokerCount = brokers.length || null;

      // "Cities covered" = distinct cities where verified brokers operate.
      const citySet = new Set<string>();
      for (const b of brokers) {
        for (const c of b.cities) {
          if (c.cityId) citySet.add(c.cityId);
          else if (c.name) citySet.add(c.name);
        }
      }
      const cities = citySet.size || null;

      const next = { listings, brokers: brokerCount, cities };
      cached = next;
      if (!cancelled) {
        setStats(next);
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return { ...stats, loading };
}
